import styled, { keyframes } from 'styled-components';

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const DropArea = styled.div<{ $isDragging: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 32px 24px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  background: ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.mode === 'dark' ? 'rgba(10,116,101,0.10)' : 'rgba(10,116,101,0.05)';
    return theme.mode === 'dark' ? '#18181b' : '#ffffff';
  }};
  transition: background 0.18s ease;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
`;

export const DropTitle = styled.p`
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f4f4f5' : '#111827')};
`;

/* ---- Układ dwóch boksów: [upload] | [biblioteka] ---- */
export const ChooseRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  @media (max-width: 560px) { flex-direction: column; }
`;

export const ChooseCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  & > * { flex: 1; }
`;

export const ChooseDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 10px 6px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#e5e7eb')};
  @media (max-width: 560px) { width: auto; height: 1px; margin: 6px 10px; }
`;

/* Ikona boksu w stylu ikon sidebara (lucide, obrys 2 px) w jasnym kółku. */
export const LibraryArt = styled.div`
  width: 56px;
  height: 56px;
  margin-bottom: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6')};
`;

/* ---- Wariant KOMPAKTOWY (inline, niski) — dwa krótkie boksy w rzędzie ---- */
export const CompactRow = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  @media (max-width: 480px) { flex-direction: column; }
`;

export const CompactBox = styled.button<{ $isDragging?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  min-height: 52px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e4e4e7' : '#374151')};
  border: 1px dashed ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.colors?.primary?.[500] || '#0a7465';
    return theme.mode === 'dark' ? '#3f3f46' : '#d1d5db';
  }};
  background: ${({ theme, $isDragging }) => {
    if ($isDragging) return theme.mode === 'dark' ? 'rgba(10,116,101,0.10)' : 'rgba(10,116,101,0.05)';
    return theme.mode === 'dark' ? '#18181b' : '#ffffff';
  }};
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;

  & svg { color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'}; flex-shrink: 0; }
  &:hover { border-color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'}; }
`;

export const CompactDivider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 8px 6px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#e5e7eb')};
  @media (max-width: 480px) { width: auto; height: 1px; margin: 4px 8px; }
`;

export const DropDescription = styled.p`
  margin: 0;
  max-width: 220px;
  min-height: 42px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1aa' : '#6b7280')};
`;

export const DropFormats = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#71717a' : '#9ca3af')};
`;

/* Główna akcja boksu: czarny, zaokrąglony przycisk 44 px (jak w formularzach). */
export const BrowseButton = styled.button`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: #111827;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) { background: #000000; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* Druga akcja (biblioteka): jasny, zaokrąglony przycisk z ramką. */
export const BrowseSecondaryButton = styled.button`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 20px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#d1d5db')};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f9fafb')};
    border-color: #9ca3af;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e4e4e7' : '#374151')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(24,24,27,0.86)' : 'rgba(255,255,255,0.86)')};
  backdrop-filter: blur(2px);
  animation: ${overlayFadeIn} 0.15s ease-out;
`;

/* ---- Krok kadrowania (react-easy-crop) ---- */
export const CropStage = styled.div`
  position: relative;
  width: 100%;
  height: 340px;
  background: #111827;
  border-radius: 14px;
  overflow: hidden;
`;

export const CropBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

export const CropCounter = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1aa' : '#6b7280')};
`;

export const ZoomRange = styled.input`
  flex: 1;
  min-width: 0;
  accent-color: ${({ theme }) => theme.colors?.primary?.[500] || '#0a7465'};
  cursor: pointer;
`;

export const CropActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

/* „Użyj oryginału" / „Anuluj" — ciemny (czarny) przycisk, spójny z AddButton. */
/* „Użyj oryginału" / „Anuluj": czarny, zaokrąglony przycisk. */
export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: #111827;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) { background: #000000; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* „Przytnij" — jasny przycisk z ramką (light), obok ciemnego „Użyj oryginału". */
/* „Przytnij": jasny, zaokrąglony przycisk z ramką. */
export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#3f3f46' : '#d1d5db')};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f9fafb')};
    border-color: #9ca3af;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
